import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";

import { formatCurrency } from "../../core/currency.core";
import { getCustomSuggestions, getTopUpSuggestions } from "../../core/product.core";
import { useTranslation } from "../../core/translation.core";
import useShop from "../../hooks/useShop.hook";
import { CurrencyString, CustomProductSuggestion, Product, ProductType } from "../../types/product";
import { ShopTiny } from "../../types/shop";
import OutlineLink from "../form/outlineLink.comp";
import { EditingMode } from "../reuseables/form.comp";

interface CustomproductItemsProps {
  editingMode: EditingMode;
  parentProduct?: Product;
  productType: ProductType;
}

export default function CustomSuggestionItems({ editingMode, parentProduct, productType }: CustomproductItemsProps) {
  const { t, msg } = useTranslation();

  if (productType !== "custom") return null;
  if (editingMode !== EditingMode.edit) return null;
  if (!parentProduct) return null;

  return (
    <div>
      <div className="block text-base font-semibold mb-2">
        {t(msg.product.customSuggestions)}&nbsp;
        <OutlineLink id="add-suggestion" href={`/product/custom-suggestions/${parentProduct.id}`} text={t(msg.form.edit)} />
      </div>
      <div className="overflow-x-scroll space-y-8 mt-4">
        <CustomSuggestionList />
        <TopUpSuggestionList parentProductId={parentProduct.id} />
      </div>
    </div>
  );
}

function CustomSuggestionList() {
  const router = useRouter();
  const { selectedShop: shop } = useShop();

  const {
    data: customSuggestions,
    isPending,
    isError,
  } = useQuery({
    queryKey: ["productCustomSuggestions", router.query.id],
    queryFn: () => getCustomSuggestions(router.query.id as string),
    enabled: router.isReady && router.query.id !== undefined,
  });

  if (isError) return null;
  if (isPending) return null;
  if (!shop) return null;

  if (!customSuggestions) return null;

  const labelLength =
    customSuggestions && customSuggestions.length > 1 && customSuggestions[0].options ? customSuggestions[0].options.length : 0;

  return (
    <div className="[&>*:not(:last-child)]:border-b" id="custom-suggestion-list">
      <ListLabel customSuggestions={customSuggestions} labelLength={labelLength} />
      {customSuggestions?.map((c, idx) => (
        <ListItem
          key={c.id}
          idx={idx}
          options={c.options.map((co) => co.value)}
          labelLength={labelLength}
          shop={shop}
          price={c.price}
        />
      ))}
    </div>
  );
}

function ListLabel({ customSuggestions, labelLength }: { customSuggestions: CustomProductSuggestion[]; labelLength: number }) {
  return (
    <div
      className="grid items-center justify-center gap-1"
      style={{ gridTemplateColumns: `40px repeat(${labelLength}, 1fr) 90px` }}
    >
      <p></p>
      <>
        {customSuggestions &&
          customSuggestions.length > 0 &&
          customSuggestions[0].options &&
          customSuggestions[0].options.map((option, idx) => (
            <p key={idx} className="font-semibold">
              {option.groupLabel}
            </p>
          ))}
      </>
      <p></p>
    </div>
  );
}

interface ListItemProps {
  idx: number;
  shop: ShopTiny;
  options: string[];
  labelLength: number;
  price: number | null;
}

function ListItem({ idx, shop, options, price, labelLength }: ListItemProps) {
  return (
    <div
      className="grid items-center justify-center gap-1"
      style={{ gridTemplateColumns: `40px repeat(${labelLength}, 1fr) 90px` }}
    >
      <p className="text-xs">#{idx + 1}</p>
      <>
        {options.map((o, idx2) => (
          <div key={idx2}>{o}</div>
        ))}
      </>
      <p>{price ? formatCurrency(price, shop?.defaultCurrency as CurrencyString) : ""}</p>
    </div>
  );
}

function TopUpSuggestionList({ parentProductId }: { parentProductId: string }) {
  const { t, msg } = useTranslation();
  const router = useRouter();
  const { selectedShop: shop } = useShop();

  const {
    data: topUpSuggestions,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["productTopUpSuggestions", router.query.id],
    queryFn: () => getTopUpSuggestions(router.query.id as string),
    enabled: router.isReady && router.query.id !== undefined,
  });

  if (isError) return null;
  if (isLoading) return null;
  if (!topUpSuggestions) return null;

  return (
    <div>
      <div id="topup-list" className="block text-base font-semibold mb-4">
        {t(msg.product.topUps)}&nbsp;
        <OutlineLink id="add-topups" href={`/product/topup-suggestions/${parentProductId}`} text={t(msg.form.edit)} />
      </div>
      <div className="[&>*:not(:last-child)]:border-b">
        {topUpSuggestions.map((c, idx) => (
          <div key={idx} className="grid grid-cols-[40px_repeat(4,_minmax(150px,_1fr))] items-center">
            <p className="text-xs basis-10">#{idx + 1}</p>
            <p>{c.name.en}</p>
            <p>{c.price ? formatCurrency(c.price, shop?.defaultCurrency as CurrencyString) : ""}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
